import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import axios from 'axios';
import {
  CONVENIENCE_DISPATCH_ALL_DELIVERIES_QUERY_KEY,
  CONVENIENCE_DISPATCH_DELIVERIES_QUERY_KEY,
} from 'src/hooks/useConvenienceDispatchDeliveries.query';
import { SHIPMENT_ORDER_LEG_RUNS_QUERY_KEY } from 'src/hooks/useQueryShipmentOrderLegRuns';
import { SHIPMENT_ORDERS_QUERY_KEY } from 'src/hooks/useQueryShipmentOrders';

/**
 * 上書き取込で「取込ファイルに無い案件」として挙がった削除候補
 * （logposeTech/log_web_issues#405 決定 6）。
 * status は承認するまで pending で、消したら deleted、残すと決めたら kept になる。
 */
export type DeletionCandidateEntity = {
  shipmentOrderId: number;
  code: string;
  unloadingName: string;
  loadingOn: string;
  allocated: boolean;
  status: 'pending' | 'deleted' | 'kept';
};

type PendingResponse = {
  importId: number | null;
  createdAt: string | null;
  ruleName: string | null;
  count: number;
  allocatedCount: number;
};

type CandidatesResponse = {
  importId: number;
  candidates: DeletionCandidateEntity[];
};

type ResolveParams = {
  importId: number;
  deleteIds: number[];
  keepIds: number[];
};

type ResolveResponse = {
  deleted: number;
  kept: number;
};

export const IMPORT_DELETION_CANDIDATES_QUERY_KEY = 'import-deletion-candidates';
export const IMPORT_PENDING_DELETION_CANDIDATES_QUERY_KEY = 'import-pending-deletion-candidates';

const basePath = '/api/v3/orders/imports';

const fetchPending = async () => {
  const response = await axios.get<PendingResponse>(`${basePath}/pending_deletion_candidates`);
  return response.data;
};

const fetchCandidates = (importId: number | null) => async () => {
  const response = await axios.get<CandidatesResponse>(`${basePath}/${importId}/deletion_candidates`);
  return response.data.candidates;
};

/** 案件一覧のバナー用。未処理の候補を持つ最新の取込を 1 件だけ返す */
export const useImportPendingDeletionCandidatesQuery = (enabled: boolean) => useQuery({
  queryKey: [IMPORT_PENDING_DELETION_CANDIDATES_QUERY_KEY],
  queryFn: fetchPending,
  enabled,
});

/** ダイアログを開いたときだけ引く。importId が無ければ取りに行かない */
export const useImportDeletionCandidatesQuery = (importId: number | null) => useQuery({
  queryKey: [IMPORT_DELETION_CANDIDATES_QUERY_KEY, importId],
  queryFn: fetchCandidates(importId),
  enabled: importId !== null,
});

export const useImportDeletionCandidatesMutation = () => {
  const queryClient = useQueryClient();

  return useMutation<ResolveResponse, Error, ResolveParams>({
    mutationFn: async ({ importId, deleteIds, keepIds }) => {
      const response = await axios.patch<ResolveResponse>(
        `${basePath}/${importId}/deletion_candidates`,
        { delete_ids: deleteIds, keep_ids: keepIds },
      );
      return response.data;
    },
    // 消すのは案件だけでなく配車もなので、案件一覧と同じ範囲を引き直す。
    // Why 一括削除（useQueryShipmentOrders の bulkDelete）と同じ顔ぶれ: 消える物が同じ。
    // 配車系は staleTime: Infinity なので、ここで落とさないと配車画面に消えた案件が残る。
    // バナーの件数は承認のたびに引き直す
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['dashboards'] });
      void queryClient.invalidateQueries({ queryKey: [SHIPMENT_ORDERS_QUERY_KEY] });
      void queryClient.invalidateQueries({ queryKey: [SHIPMENT_ORDER_LEG_RUNS_QUERY_KEY] });
      void queryClient.invalidateQueries({ queryKey: [CONVENIENCE_DISPATCH_DELIVERIES_QUERY_KEY] });
      void queryClient.invalidateQueries({ queryKey: [CONVENIENCE_DISPATCH_ALL_DELIVERIES_QUERY_KEY] });
      void queryClient.invalidateQueries({ queryKey: [IMPORT_PENDING_DELETION_CANDIDATES_QUERY_KEY] });
      void queryClient.invalidateQueries({ queryKey: [IMPORT_DELETION_CANDIDATES_QUERY_KEY] });
    },
  });
};
